import { registerLocaleData } from '@angular/common';
import localeDataAr from '@angular/common/locales/es-AR';
import localeDataCl from '@angular/common/locales/es-CL';
import localeDataCo from '@angular/common/locales/es-CO';
import localeDataMx from '@angular/common/locales/es-MX';
import localeDataPe from '@angular/common/locales/es-PE';
import localeDataUy from '@angular/common/locales/es-UY';
import localeExtraDataAr from '@angular/common/locales/extra/es-AR';
import localeExtraDataCl from '@angular/common/locales/extra/es-CL';
import localeExtraDataCo from '@angular/common/locales/extra/es-CO';
import localeExtraDataMx from '@angular/common/locales/extra/es-MX';
import localeExtraDataPe from '@angular/common/locales/extra/es-PE';
import localeExtraDataUy from '@angular/common/locales/extra/es-UY';
import { Injectable } from '@angular/core';
import { ValidatorFn } from '@angular/forms';
import { SessionStorageService } from 'ngx-webstorage';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { removeSpecialCharacters } from '../../shared/helpers/helpers';
import { NumberValidator } from '../../shared/validators/number.validator';
import { LocalizationService } from '../localization/localization.service';
import { CountryModel } from '../user/country.model';
import { StoreModel, IStore } from '../user/store.model';
import { UserService } from '../user/user.service';
import { plainToClass } from 'class-transformer';
import { BrandService } from './../../core/user/brand.service';


@Injectable({
  providedIn: 'root'
})
export class ContextService {
  private _labelsLoaded = new BehaviorSubject<boolean>(false);
  country: string;
  store: StoreModel;
  brand: any;

  readonly COUNTRIES = {
    CL: {
      shortLabel: 'cl',
      label: 'Chile',
      localeData: localeDataCl,
      localeExtraData: localeExtraDataCl,
      priceValidations: [NumberValidator.integer],
      priceDecimals: '1.0-0',
    },
    AR: {shortLabel: 'ar', label: 'Argentina', localeData: localeDataAr, localeExtraData: localeExtraDataAr},
    UY: {shortLabel: 'uy', label: 'Uruguay', localeData: localeDataUy, localeExtraData: localeExtraDataUy},
    PE: {
      shortLabel: 'pe',
      label: 'Perú',
      localeData: localeDataPe,
      localeExtraData: localeExtraDataPe,
      priceValidations: [],
      priceDecimals: '1.2-2'
    },
    CO: {shortLabel: 'co', label: 'Colombia', localeData: localeDataCo, localeExtraData: localeExtraDataCo},
    MX: {shortLabel: 'mx', label: 'México', localeData: localeDataMx, localeExtraData: localeExtraDataMx},
    TEST: {shortLabel: 'test', label: 'Test', localeData: localeDataCl, localeExtraData: localeExtraDataCl}
  };

  
  constructor(
    private storageService: SessionStorageService,
    private userService: UserService,
    private localizationService: LocalizationService,
    private brandService: BrandService
  ) {
    const selectedCountry = this.storageService.retrieve('country');
    let selectedStore = plainToClass(StoreModel, this.storageService.retrieve('store') as StoreModel);
    
    this.userService.getUser().subscribe(user => {
      let country = 'CL';
      
      if (selectedCountry && user && user.lstTenantIds.includes(selectedCountry)) {
        country = selectedCountry;
        
      } else if (user && user.defautlTenantId) {
        country = user.defautlTenantId;
      }
      
      if (!selectedStore && user && user.stores && user.stores.length) {
        selectedStore = user.stores[0];
      }
      
      this.changeCountry(country);
      this.changeStore(selectedStore);
    });
  }

  
  changeCountry(tenant: string, reload = false): void {
    if (reload) {
      this.storageService.store('country', tenant);
      this.storageService.clear('store');
      window.location.reload();
      return;
    }
    this.country = tenant;
    this.loadLocale();
    this.localizationService.loadLabels(this.country.toLowerCase(), () => {
      this._labelsLoaded.next(true);
      this._labelsLoaded.complete();
    });
  }

  changeStore(store: StoreModel, reload = false): void {
    if (store) {
      this.storageService.store('store', store);
    }
    if (reload) {
      window.location.reload();
      return;
    }
    this.store = store;
  }

  private async loadLocale(): Promise<void> {
    registerLocaleData(this.COUNTRIES[this.country].localeData, this.locale, this.COUNTRIES[this.country].localeExtraData);
  }

  getTenancyModel(lstTenantIds: string[]): CountryModel[] {
    let countries = [];
    if (lstTenantIds && lstTenantIds.length) {
      lstTenantIds.forEach(tenant => {
        countries = countries.concat(this.COUNTRIES[tenant]);
      });
    }
    return countries;
  }

  get priceValidations(): ValidatorFn[] {
    if (this.COUNTRIES[this.country].priceValidations) {
      return this.COUNTRIES[this.country].priceValidations;
    }
    return [NumberValidator.number(2)];
  }

  get locale(): string {
    return 'es-' + this.country.toUpperCase();
  }

  formatIdentificationNumber(identificationNumber: string | number, removeMantissa: boolean = false): string {
    const aux = removeSpecialCharacters(identificationNumber);
    switch (this.country) {
      case 'CL':
        if (removeMantissa) {
          return `${aux.substr(0, aux.length - 1)}`;
        }
        return `${aux.substr(0, aux.length - 1)}-${aux.substr(aux.length - 1)}`;
      default:
        return aux;
    }
  }

  formatPriceValues(value: number): number {
    switch (this.country) {
      case 'CL':
        value = +value;
        return +value.toFixed();
      default:
        value = +value;
        return +value.toFixed(2);
    }
  }

  get localeData(): string {
    return this.COUNTRIES[this.country].localeData;
  }

  get localeExtraData(): string {
    return this.COUNTRIES[this.country].localeExtraData;
  }

  get priceDecimals(): string {
    return this.COUNTRIES[this.country].priceDecimals || '1.2-2';
  }

  get labesLoaded(): Observable<boolean> {
    return this._labelsLoaded.asObservable();
  }

  get canSellProducts(): boolean {
    switch (this.country) {
      case 'CL':
        return true;
      case 'PE':
        return true;
      default:
        break;
    }
  }

  getStoreLabel(store: IStore): string {
    switch (this.country) {
      case 'PE':
        return `${store.store.identificationNumber} - ${store.store.name} - Marca:    `;
      default:
        return store.label;
    }
  }
}
